import React, { useEffect, useState } from 'react';
// Leaflet map imports and marker icon fix
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import CountUp from 'react-countup';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Box,
  Chip,
  Avatar,
  Stack,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Construction,
  Schedule,
  HourglassEmpty,
  CalendarToday,
  Visibility,
  ArrowForward,
  Security,
  Speed,
  EmojiEvents,
  PeopleAlt,
  TrendingUp,
  Star,
  Phone,
  Build,
  CheckCircle
} from '@mui/icons-material';
import { getAllProjects, getAllServices, resolveImageUrl } from '../utils/api';

// Fix default icon for leaflet markers
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});
// Modern Hero Section with slider and overlay

// Construction-themed animated icons for slider
const sliderIcons = [
  Construction,
  Build,
  Security,
  Speed,
];
const HeroSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
  color: 'white',
  overflow: 'hidden',
  marginTop: '-90px', // Pull slider up to overlap with navbar
  paddingTop: '90px', // Add padding to maintain content position
  [theme.breakpoints.down('md')]: {
    minHeight: '80vh',
    paddingTop: '70px',
    marginTop: '-70px',
  },
  [theme.breakpoints.down('sm')]: {
    minHeight: '60vh',
    paddingTop: '56px',
    marginTop: '-56px',
  },
}));

const SliderContainer = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1,
});

const SlideImage = styled(motion.div)(({ isActive }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  opacity: isActive ? 1 : 0,
  zIndex: 1,
  filter: 'brightness(1.1)',
  transition: 'opacity 0.6s cubic-bezier(0.4,0,0.2,1)',
  pointerEvents: 'none',
}));

const SliderOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.2)', // Lighter overlay for brighter slider images
  zIndex: 2,
});

const SliderDots = styled(Box)({
  position: 'absolute',
  bottom: '2rem',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  gap: '0.5rem',
  zIndex: 4,
});

const SliderDot = styled(Box)(({ isActive, theme }) => ({
  width: 12,
  height: 12,
  borderRadius: '50%',
  backgroundColor: isActive ? 'white' : 'rgba(255, 255, 255, 0.5)',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'white',
    transform: 'scale(1.2)',
  },
}));

const SlideContent = styled(motion.div)(({ isActive }) => ({
  opacity: isActive ? 1 : 0,
  position: 'absolute',
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  pointerEvents: isActive ? 'auto' : 'none',
}));

const HeroContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 3,
  textAlign: 'left',
  maxWidth: '700px', // Reduced from 800px
}));

// Modern card styling
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.shadows[10],
  },
}));

const StatsSection = styled(Box)(({ theme }) => ({
  background: '#EAF7FF', // Custom blue background
  color: '#2C3E50', // Dark text for better contrast
  padding: theme.spacing(8, 0),
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'url("data:image/svg+xml,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 100 100\'><defs><pattern id=\'grain\' width=\'100\' height=\'100\' patternUnits=\'userSpaceOnUse\'><circle cx=\'50\' cy=\'50\' r=\'1\' fill=\'%232C3E50\' opacity=\'0.05\'/></pattern></defs><rect width=\'100\' height=\'100\' fill=\'url(%23grain)\'/></svg>")',
    opacity: 0.1,
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  color: '#2980b9', // Professional dark color
  marginBottom: theme.spacing(1),
}));

const Home = () => {
  // Example location: replace with your actual business coordinates
  const businessLocation = {
    lat: 51.589788513258654, // 30 Mossford Green Barkingside ig6 2bj
    lng: 0.07755707266227503
  };
  const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${businessLocation.lat},${businessLocation.lng}`;
  const navigate = useNavigate();
  const theme = useTheme();
  const [projects, setProjects] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Slider data with different images and texts
  const slides = [
    {
      image: "/sliderImages/11.jpeg",
      title: "Building Your Dreams Into Reality",
      subtitle: "Professional construction services with over 15 years of experience. From residential homes to commercial buildings, we deliver quality that lasts."
    },
    {
      image: "/sliderImages/12.jpeg",
      title: "Modern Construction Solutions",
      subtitle: "Innovative building techniques and cutting-edge technology to create sustainable and efficient structures for the future."
    },
    {
      image: "/sliderImages/15.jpg",
      title: "Expert Craftsmanship",
      subtitle: "Our skilled professionals bring decades of experience to every project, ensuring superior quality and attention to detail."
    },
    {
      image: "/sliderImages/30.jpg",
      title: "Commercial Excellence",
      subtitle: "From office buildings to retail spaces, we create commercial environments that inspire productivity and success."
    }
  ];

  // Auto-slide functionality
  useEffect(() => {
    const slideInterval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(slideInterval);
  }, [slides.length]);

  // Helper functions for project display
  const formatDate = (dateString) => {
    if (!dateString || dateString === '' || dateString === '0000-00-00' || dateString === null) {
      return null;
    }

    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return null;
    }

    return date.toLocaleDateString();
  };

  const getProjectStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'success';
      case 'in progress':
      case 'in-progress':
      case 'ongoing': return 'warning';
      case 'pending':
      case 'planning':
      case 'upcoming': return 'info';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getProjectStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return <CheckCircle />;
      case 'in progress':
      case 'in-progress':
      case 'ongoing': return <Schedule />;
      case 'pending':
      case 'planning':
      case 'upcoming': return <HourglassEmpty />;
      default: return <HourglassEmpty />;
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  const fetchData = async () => {
    try {
      const [projectsResponse, servicesResponse] = await Promise.all([
        getAllProjects(),
        getAllServices()
      ]);

      const projectsData = projectsResponse?.data?.projects || projectsResponse?.projects || [];
      const servicesData = servicesResponse?.data?.services || servicesResponse?.services || servicesResponse?.data || [];

      setProjects(Array.isArray(projectsData) ? projectsData.slice(0, 6) : []);
      setServices(Array.isArray(servicesData) ? servicesData.slice(0, 6) : []);

    } catch (error) {
      setProjects([]);
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: Security,
      title: 'Licensed & Insured',
      description: 'Fully licensed and insured construction company with all necessary certifications.'
    },
    {
      icon: Speed,
      title: 'Fast Delivery',
      description: 'Efficient project management ensuring timely completion without compromising quality.'
    },
    {
      icon: EmojiEvents,
      title: 'Quality Assured',
      description: 'Premium materials and industry best practices for exceptional construction quality.'
    },
    {
      icon: PeopleAlt,
      title: 'Expert Team',
      description: 'Experienced professionals with decades of construction and engineering expertise.'
    }
  ];

  const stats = [
    { number: '500+', label: 'Projects Completed', icon: Construction },
    { number: '15+', label: 'Years Experience', icon: TrendingUp },
    { number: '98%', label: 'Client Satisfaction', icon: Star },
    { number: '24/7', label: 'Customer Support', icon: Phone }
  ];

  return (
    <>
      {/* Modern Hero Section with Animated Slider */}
      <HeroSection>
        {/* Slider Container with Framer Motion */}
        <SliderContainer>
          {slides.map((slide, index) => (
            <SlideImage
              key={index}
              isActive={currentSlide === index}
              style={{ backgroundImage: `url("${slide.image}")` }}
            />
          ))}
          <SliderOverlay />
        </SliderContainer>

        {/* Animated Slider Content */}
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 3 }}>
          <AnimatePresence initial={false} mode="wait">
            {slides.map((slide, index) =>
              currentSlide === index && (
                <SlideContent
                  key={index}
                  isActive={true}
                  as={motion.div}
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -40 }}
                  transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
                >
                  <HeroContent>
                    <Stack spacing={3} sx={{ pl: { xs: 1, sm: 2, md: 4, lg: 3, xl: 5 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, }}>
                        {/* Animated construction icon */}
                        <motion.div
                          initial={{ x: -60, opacity: 0, rotate: -20 }}
                          animate={{ x: 0, opacity: 1, rotate: 0 }}
                          exit={{ x: 60, opacity: 0, rotate: 20 }}
                          transition={{ duration: 0.7, delay: 0.1 }}
                          style={{ display: 'flex', alignItems: 'center' }}
                        >
                          {React.createElement(sliderIcons[index % sliderIcons.length], {
                            style: {
                              fontSize: window.innerWidth < 600 ? 25 : window.innerWidth < 900 ? 35 : window.innerWidth < 1200 ? 45 : 55,
                              color: theme.palette.secondary.main,
                              filter: 'drop-shadow(0 4px 16px rgba(52,152,219,0.18))',
                              marginRight: 8,
                              transition: 'font-size 0.3s',
                            },
                          })}
                        </motion.div>
                        <Typography
                          variant="h1"
                          sx={{
                            fontSize: { xs: '1rem', sm: '1.2rem', md: '1.5rem', lg: '1.8rem', xl: '2rem' },
                            fontWeight: 700,
                            lineHeight: 1.1,
                            letterSpacing: '-0.02em',
                            textShadow: '0 0 10px rgba(255, 255, 255, 0.3)',
                            color: 'rgba(255, 255, 255, 1)',
                            transition: 'font-size 0.3s',
                          }}
                        >
                          {slide.title.split(' ').slice(0, 2).join(' ')}
                          <Box component="span" sx={{ display: 'block', color: theme.palette.secondary.main }}>
                            {slide.title.split(' ').slice(2).join(' ')}
                          </Box>
                        </Typography>
                      </Box>
                      <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -30 }}
                        transition={{ duration: 0.7, delay: 0.2 }}
                      >
                        <Typography
                          variant="h5"
                          sx={{
                            fontSize: { xs: '0.8rem', sm: '0.9rem', md: '0.9rem', lg: '1rem', xl: '1.2rem' },
                            fontWeight: 400,
                            opacity: 1,
                            maxWidth: { xs: '90vw', sm: '500px' },
                            lineHeight: 1.6,
                            transition: 'font-size 0.3s',
                          }}
                        >
                          {slide.subtitle}
                        </Typography>
                      </motion.div>
                      <Stack direction={{ xs: 'row', sm: 'row' }} spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mt: { xs: 2, sm: 3, md: 4 } }}>
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 20 }}
                          transition={{ duration: 0.6, delay: 0.3 }}
                        >
                          <Button
                            variant="contained"
                            size="small"
                            endIcon={<ArrowForward />}
                            onClick={() => navigate('/contact')}
                            sx={{
                              py: { xs: 0.7, sm: 1, md: 1.1, lg: 1 },
                              px: { xs: 1.5, sm: 2.5, md: 3, lg: 2.5 },
                              fontSize: { xs: '0.5rem', sm: '0.7rem', md: '0.8rem', lg: '0.85rem', xl: '0.95rem' },
                              borderRadius: '50px',
                              background: theme.palette.secondary.main,
                              color: '#fff',
                              fontWeight: 600,
                              boxShadow: '0 4px 16px rgba(52,152,219,0.18)',
                              minWidth: { xs: 80, sm: 50, md: 110, lg: 100, xl: 110 },
                              '&:hover': {
                                background: theme.palette.secondary.dark || '#217dbb',
                                transform: 'translateY(-2px)',
                              },
                            }}
                          >
                            Get Free Quote
                          </Button>
                        </motion.div>
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 20 }}
                          transition={{ duration: 0.6, delay: 0.4 }}
                        >
                          <Button
                            variant="outlined"
                            size="small"
                            endIcon={<Visibility />}
                            onClick={() => navigate('/projects')}
                            sx={{
                              py: { xs: 0.7, sm: 1, md: 1.1, lg: 1 },
                              px: { xs: 1.5, sm: 2.5, md: 3, lg: 2.5 },
                              fontSize: { xs: '0.5rem', sm: '0.7rem', md: '0.8rem', lg: '0.85rem', xl: '0.95rem' },
                              borderRadius: '50px',
                              borderColor: 'rgba(255, 255, 255, 0.5)',
                              color: 'white',
                              fontWeight: 600,
                              background: 'rgba(255,255,255,0.04)',
                              minWidth: { xs: 80, sm: 50, md: 110, lg: 100, xl: 110 },
                              '&:hover': {
                                borderColor: 'white',
                                background: 'rgba(255, 255, 255, 0.1)',
                                transform: 'translateY(-2px)',
                              },
                            }}
                          >
                            View Our Work
                          </Button>
                        </motion.div>
                      </Stack>
                    </Stack>
                  </HeroContent>
                </SlideContent>
              )
            )}
          </AnimatePresence>
        </Container>

        {/* Slider Dots */}
        <SliderDots>
          {slides.map((_, index) => (
            <SliderDot
              key={index}
              isActive={currentSlide === index}
              onClick={() => setCurrentSlide(index)}
            />
          ))}
        </SliderDots>
      </HeroSection>

      {/* Stats Section */}
      <StatsSection>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {stats.map((stat, index) => {
              const IconComponent = stat.icon;
              // Modern stat card look
              let endValue = 0;
              let suffix = '';
              let statColor = '#2980b9';
              let iconBg = 'white';
              let iconShadow = '0 8px 32px rgba(52,152,219,0.18)';
              // Value and suffix logic
              if (stat.label === 'Customer Support') {
                endValue = 24;
                suffix = '/7';
              } else if (stat.label === 'Client Satisfaction') {
                endValue = 98;
                suffix = '%';
              } else if (typeof stat.number === 'string') {
                const match = stat.number.match(/\d+/);
                endValue = match ? parseInt(match[0], 10) : 0;
                suffix = /\+/.test(stat.number) ? '+' : '';
              } else if (typeof stat.number === 'number') {
                endValue = stat.number;
              }
              return (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Box
                    sx={{
                      background: 'white',
                      borderRadius: 5,
                      boxShadow: '0 4px 24px rgba(44,62,80,0.07)',
                      p: 4,
                      textAlign: 'center',
                      position: 'relative',
                      zIndex: 2,
                      transition: 'box-shadow 0.3s, transform 0.3s',
                      transform: 'rotateY(0deg) scale(1)',
                      '&:hover': {
                        boxShadow: '0 12px 32px 0 rgba(52,152,219,0.18), 0 8px 32px rgba(44,62,80,0.18)',
                        transform: 'translateY(-10px) scale(1.04) rotateY(-8deg) skewY(-2deg)',
                      },
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        left: 18,
                        right: 18,
                        bottom: 10,
                        height: 18,
                        borderRadius: '50%',
                        background: 'radial-gradient(ellipse at center, rgba(44,62,80,0.10) 0%, rgba(44,62,80,0.02) 100%)',
                        filter: 'blur(2.5px)',
                        zIndex: 1,
                        opacity: 0.7,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 72,
                        height: 72,
                        mx: 'auto',
                        mb: 2,
                        borderRadius: '50%',
                        background: iconBg,
                        boxShadow: iconShadow,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: `3px solid ${statColor}`,
                        transition: 'box-shadow 0.3s',
                      }}
                    >
                      <IconComponent sx={{ fontSize: 38, color: statColor }} />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 800,
                        mb: 1,
                        color: statColor,
                        fontSize: { xs: '2.1rem', md: '2.7rem' },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        letterSpacing: '-0.03em',
                        lineHeight: 1.1,
                      }}
                    >
                      {stat.label === 'Customer Support' ? (
                        <span style={{ display: 'inline-flex', alignItems: 'baseline' }}>
                          <CountUp end={24} duration={1.6} start={0} enableScrollSpy scrollSpyOnce />
                          <span style={{ fontSize: '1.7rem', fontWeight: 700, marginLeft: 2, color: statColor }}>/7</span>
                        </span>
                      ) : (
                        <CountUp
                          end={endValue}
                          duration={1.6}
                          suffix={suffix}
                          start={0}
                          enableScrollSpy
                          scrollSpyOnce
                        />
                      )}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        opacity: 0.92,
                        fontWeight: 600,
                        color: '#2C3E50',
                        fontSize: '1.1rem',
                        letterSpacing: '0.01em',
                        mt: 1,
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </StatsSection>

      {/* Services Section */}
      <Box sx={{ background: '#F8F9FA', py: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <SectionTitle variant="h2" sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
              Our Services
            </SectionTitle>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto', mt: 2 }}>
              From residential homes to commercial buildings, we provide comprehensive construction solutions
            </Typography>
          </Box>

          {!loading && services.length > 0 && (
            <Grid container spacing={4}>
              {services.map((service, index) => (
                <Grid item xs={12} sm={6} md={4} key={service.id || index}>
                  <StyledCard>
                    <CardContent sx={{ p: 4, flexGrow: 1 }}>
                      {/* Icon and Title on same line */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                        <Box sx={{
                          width: 60,
                          height: 60,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          {service.icon_url ? (
                            <Avatar
                              src={resolveImageUrl(service.icon_url)}
                              sx={{
                                width: 60,
                                height: 60,
                                background: 'transparent',
                              }}
                            />
                          ) : (
                            <Avatar
                              sx={{
                                width: 60,
                                height: 60,
                                background: '#DDE6ED',
                                color: '#2C3E50',
                              }}
                            >
                              <Build sx={{ fontSize: 30 }} />
                            </Avatar>
                          )}
                        </Box>
                        <Typography variant="h5" component="h3" sx={{ fontWeight: 600, flex: 1, color: '#2C3E50' }}>
                          {service.title}
                        </Typography>
                      </Box>

                      {/* Features */}
                      {service.features && service.features.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {service.features.slice(0, 3).map((feature, featureIndex) => (
                              <Chip
                                key={featureIndex}
                                label={feature}
                                size="small"
                                variant="outlined"
                                color="primary"
                                sx={{
                                  borderRadius: 3,
                                  fontSize: '0.7rem',
                                  height: 24
                                }}
                              />
                            ))}
                            {service.features.length > 3 && (
                              <Chip
                                label={`+${service.features.length - 3} more`}
                                size="small"
                                variant="filled"
                                color="secondary"
                                sx={{
                                  borderRadius: 3,
                                  fontSize: '0.7rem',
                                  height: 24
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                      )}

                      {/* Description - Truncated to 3 lines */}
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          lineHeight: 1.7,
                        }}
                      >
                        {service.description.slice(0, 150) + "..."}
                      </Typography>
                    </CardContent>

                    <CardActions sx={{ justifyContent: 'center', pb: 3 }}>
                      <Button
                        variant="outlined"
                        startIcon={<Visibility />}
                        onClick={() => navigate(`/services/${service.slug || service.id}`)}
                        sx={{
                          borderRadius: 2,
                          fontWeight: 600,
                          color: '#2C3E50',
                          borderColor: '#2C3E50',
                          '&:hover': {
                            backgroundColor: '#2C3E50',
                            color: 'white',
                          },
                        }}
                      >
                        View Details
                      </Button>
                    </CardActions>
                  </StyledCard>
                </Grid>
              ))}
            </Grid>
          )}

          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Button
              variant="outlined"
              size="large"
              endIcon={<ArrowForward />}
              onClick={() => navigate('/services')}
              sx={{
                borderRadius: '50px',
                px: 4,
                py: 1.5,
                fontWeight: 600,
                borderColor: '#2C3E50', // Professional border color
                color: '#2C3E50',
                '&:hover': {
                  background: '#2C3E50', // Dark background on hover
                  color: 'white',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              View All Services
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Easy Steps Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #eaf6ff 100%)',
          py: { xs: 3, md: 6 },
          position: 'relative',
          overflow: 'visible',
          perspective: '1200px',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 100 100\'><defs><pattern id=\'stepsbg\' width=\'100\' height=\'100\' patternUnits=\'userSpaceOnUse\'><circle cx=\'50\' cy=\'50\' r=\'1\' fill=\'%232C3E50\' opacity=\'0.04\'/></pattern></defs><rect width=\'100\' height=\'100\' fill=\'url(%23stepsbg)\'/></svg>")',
            opacity: 0.18,
            zIndex: 1,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 7 }}>
            <SectionTitle variant="h2" sx={{ fontSize: { xs: '2.2rem', md: '2.8rem' }, color: '#2980b9', mb: 2 }}>
              Easy Steps to Get Started With Us
            </SectionTitle>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto', mt: 1 }}>
              Begin your construction journey in just a few simple steps. We make it easy, transparent, and stress-free.
            </Typography>
          </Box>
          <Grid container spacing={4} justifyContent="center" sx={{ perspective: '1200px' }}>
            {[
              {
                icon: <Phone sx={{ fontSize: 34, color: '#fff' }} />,
                iconBg: '#2980b9',
                shadow: '0 12px 32px 0 rgba(79,140,255,0.18), 0 2px 8px 0 rgba(30,60,114,0.10)',
                title: 'Contact Us',
                titleColor: '#2980b9',
                desc: 'Reach out via phone, email, or our online form. Our team is ready to listen to your needs.'
              },
              {
                icon: <EmojiEvents sx={{ fontSize: 34, color: '#fff' }} />,
                iconBg: '#2980b9',
                shadow: '0 12px 32px 0 rgba(79,140,255,0.18), 0 2px 8px 0 rgba(30,60,114,0.10)',
                title: 'Free Estimate',
                titleColor: '#2980b9',
                desc: 'Get a transparent, no-obligation quote tailored to your project requirements.'
              },
              {
                icon: <Schedule sx={{ fontSize: 34, color: '#fff' }} />,
                iconBg: '#2980b9',
                shadow: '0 12px 32px 0 rgba(79,140,255,0.18), 0 2px 8px 0 rgba(30,60,114,0.10)',
                title: 'Planning & Timeline',
                titleColor: '#2980b9',
                desc: 'We collaborate to plan every detail and set a clear, realistic timeline.'
              },
              {
                icon: <Build sx={{ fontSize: 34, color: '#fff' }} />,
                iconBg: '#2980b9',
                shadow: '0 12px 32px 0 rgba(79,140,255,0.18), 0 2px 8px 0 rgba(30,60,114,0.10)',
                title: 'Work Begins',
                titleColor: '#2980b9',
                desc: 'Our expert team gets to work, keeping you updated at every stage until completion.'
              },
            ].map((step, idx) => (
              <Grid item xs={12} sm={6} md={3} key={idx} sx={{ perspective: '1200px' }}>
                <motion.div
                  initial={{ opacity: 0, y: 60, rotateY: 30, scale: 0.92 }}
                  whileInView={{ opacity: 1, y: 0, rotateY: 0, scale: 1 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.8, delay: idx * 0.18, type: 'spring', stiffness: 60 }}
                  style={{ height: '100%', transformStyle: 'preserve-3d' }}
                >
                  <Box
                    sx={{
                      background: 'white',
                      borderRadius: 5,
                      boxShadow: `${step.shadow}, 0 1.5px 8px 0 rgba(44,62,80,0.10)`,
                      p: 4,
                      textAlign: 'center',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      position: 'relative',
                      zIndex: 2,
                      transition: 'box-shadow 0.3s, transform 0.3s',
                      transform: 'rotateY(0deg) scale(1)',
                      '&:hover': {
                        boxShadow: `${step.shadow}, 0 8px 32px rgba(52,152,219,0.18)`,
                        transform: 'translateY(-10px) scale(1.04) rotateY(-8deg) skewY(-2deg)',
                      },
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        left: 18,
                        right: 18,
                        bottom: 10,
                        height: 18,
                        borderRadius: '50%',
                        background: 'radial-gradient(ellipse at center, rgba(44,62,80,0.10) 0%, rgba(44,62,80,0.02) 100%)',
                        filter: 'blur(2.5px)',
                        zIndex: 1,
                        opacity: 0.7,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 62,
                        height: 62,
                        mb: 2.5,
                        borderRadius: '18px',
                        background: step.iconBg,
                        boxShadow: '0 10px 32px 0 rgba(44,62,80,0.13), 0 2px 8px 0 rgba(44,62,80,0.10)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'box-shadow 0.3s',
                        border: '2.5px solid #fff',
                        position: 'relative',
                        zIndex: 2,
                        transform: 'translateY(-12px) scale(1.08) rotateX(8deg)',
                        boxShadow: `${step.shadow}, 0 2px 8px 0 rgba(44,62,80,0.10)`,
                      }}
                    >
                      {step.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 800,
                        mb: 1,
                        color: step.titleColor,
                        fontSize: { xs: '1.13rem', md: '1.28rem' },
                        letterSpacing: '-0.01em',
                        textTransform: 'uppercase',
                        lineHeight: 1.2,
                        fontFamily: 'Montserrat, sans-serif',
                        letterSpacing: '0.03em',
                        textShadow: '0 2px 8px rgba(44,62,80,0.08)',
                        zIndex: 2,
                      }}
                    >
                      {step.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        lineHeight: 1.7,
                        fontWeight: 500,
                        fontSize: '1.01rem',
                        opacity: 0.92,
                        mt: 1,
                        zIndex: 2,
                        textShadow: '0 1.5px 6px rgba(44,62,80,0.07)',
                      }}
                    >
                      {step.desc}
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Projects Section */}
      <Box sx={{
        width: '100%',
        position: 'relative',
        py: 8,
        background: 'linear-gradient(135deg, #232a34 0%, #1a1a1a 100%)',
        boxShadow: '0 8px 40px 0 rgba(44,62,80,0.18)',
        borderRadius: 0,
        overflow: 'hidden',
        zIndex: 10,
        '::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
          background: 'url("data:image/svg+xml,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 100 100\'><defs><radialGradient id=\'bg2\' cx=\'50%\' cy=\'50%\' r=\'80%\'><stop offset=\'0%\' stop-color=\'%233498db\' stop-opacity=\'0.18\'/><stop offset=\'100%\' stop-color=\'%23232a34\' stop-opacity=\'0.0\'/></radialGradient></defs><rect width=\'100\' height=\'100\' fill=\'url(%23bg2)\'/></svg>")',
          opacity: 0.8,
        },
      }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <SectionTitle variant="h2" sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
              Recent Projects
            </SectionTitle>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto', mt: 2 }}>
              Take a look at some of our completed construction projects that showcase our expertise
            </Typography>
          </Box>

          {!loading && projects.length > 0 && (
            <Grid container spacing={4}>
              {projects.map((project, index) => (
                <Grid item xs={12} sm={6} md={4} key={project.id || index}>
                  <StyledCard>
                    <CardMedia
                      component="img"
                      height="250"
                      image={project.images && project.images.length > 0
                        ? resolveImageUrl(project.images[0].image_url)
                        : resolveImageUrl(project.image_url)
                      }
                      alt={project.title}
                    />
                    <CardContent sx={{ p: 3, flexGrow: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: theme.palette.primary.main }}>
                        {project.title}
                      </Typography>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                        {(project?.description || '').slice(0, 200) + '...'}
                      </Typography>

                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Chip
                          icon={getProjectStatusIcon(project.status)}
                          label={project.status}
                          color={getProjectStatusColor(project.status)}
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                        {project.completion_date && formatDate(project.completion_date) && (
                          <Chip
                            icon={<CalendarToday />}
                            label={formatDate(project.completion_date)}
                            variant="outlined"
                            size="small"
                          />
                        )}
                      </Stack>
                    </CardContent>

                    <CardActions sx={{ p: 3, pt: 0 }}>
                      <Button
                        size="small"
                        startIcon={<Visibility />}
                        onClick={() => navigate(`/projects/${project.slug || project.id}`)}
                        sx={{
                          borderRadius: '20px',
                          fontWeight: 600,
                          color: '#2C3E50', // Professional dark text
                          '&:hover': {
                            background: 'rgba(44, 62, 80, 0.1)', // Light professional hover
                          },
                        }}
                      >
                        View Details
                      </Button>
                      {project.images && project.images.length > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                          {project.images.length} image{project.images.length !== 1 ? 's' : ''}
                        </Typography>
                      )}
                    </CardActions>
                  </StyledCard>
                </Grid>
              ))}
            </Grid>
          )}

          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForward />}
              onClick={() => navigate('/projects')}
              sx={{
                borderRadius: '50px',
                px: 4,
                py: 1.5,
                fontWeight: 600,
                background: theme.palette.secondary.main, // Use theme color for consistency
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(52, 152, 219, 0.3)',
                },
              }}
            >
              View All Projects
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 7 }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <SectionTitle variant="h2" sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
            Why Choose Us
          </SectionTitle>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto', mt: 2 }}>
            We combine years of experience with modern techniques to deliver exceptional construction services
          </Typography>
        </Box>
        
        <Grid container spacing={4}>
          {features.map((feature, index) => {
            const IconComponent = feature.icon;
            // Modern icon backgrounds and color for each feature
            let iconBg = 'linear-gradient(135deg, #e0eafc 0%, #cfdef3 100%)';
            let iconColor = '#3498db';
            let iconShadow = '0 8px 32px rgba(52,152,219,0.18)';
            if (feature.title === 'Licensed & Insured') {
              iconBg = 'linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 100%)';
              iconColor = '#1abc9c';
              iconShadow = '0 8px 32px rgba(26,188,156,0.18)';
            } else if (feature.title === 'Fast Delivery') {
              iconBg = 'linear-gradient(135deg, #fffbe6 0%, #ffe082 100%)';
              iconColor = '#f39c12';
              iconShadow = '0 8px 32px rgba(243,156,18,0.18)';
            } else if (feature.title === 'Quality Assured') {
              iconBg = 'linear-gradient(135deg, #fff3e0 0%, #ffd180 100%)';
              iconColor = '#e67e22';
              iconShadow = '0 8px 32px rgba(230,126,34,0.18)';
            } else if (feature.title === 'Expert Team') {
              iconBg = 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)';
              iconColor = '#2980b9';
              iconShadow = '0 8px 32px rgba(41,128,185,0.18)';
            }
            return (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Box
                  sx={{
                    textAlign: 'center',
                    height: '100%',
                    background: 'white',
                    borderRadius: 5,
                    boxShadow: '0 4px 24px rgba(44,62,80,0.07)',
                    p: 4,
                    transition: 'box-shadow 0.3s, transform 0.3s',
                    transform: 'rotateY(0deg) scale(1)',
                    position: 'relative',
                    zIndex: 2,
                    '&:hover': {
                      boxShadow: '0 12px 32px 0 rgba(52,152,219,0.18), 0 8px 32px rgba(44,62,80,0.18)',
                      transform: 'translateY(-10px) scale(1.04) rotateY(-8deg) skewY(-2deg)',
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      left: 18,
                      right: 18,
                      bottom: 10,
                      height: 18,
                      borderRadius: '50%',
                      background: 'radial-gradient(ellipse at center, rgba(44,62,80,0.10) 0%, rgba(44,62,80,0.02) 100%)',
                      filter: 'blur(2.5px)',
                      zIndex: 1,
                      opacity: 0.7,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      mx: 'auto',
                      mb: 2,
                      borderRadius: '50%',
                      background: iconBg,
                      boxShadow: iconShadow,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: `3px solid ${iconColor}`,
                      transition: 'box-shadow 0.3s',
                    }}
                  >
                    <IconComponent sx={{ fontSize: 38, color: iconColor }} />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      mb: 2,
                      color: iconColor,
                      fontSize: { xs: '1.2rem', md: '1.35rem' },
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      lineHeight: 1.7,
                      fontWeight: 500,
                      fontSize: '1.05rem',
                      opacity: 0.92,
                    }}
                  >
                    {feature.description}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Container>

      {/* Map Section - Full Width */}
      <Box sx={{
        width: '100%',
        background: '#fff',
        color: '#2C3E50',
        py: 5,
        position: 'relative',
        overflowX: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 100 100\'><defs><pattern id=\'grain\' width=\'100\' height=\'100\' patternUnits=\'userSpaceOnUse\'><circle cx=\'50\' cy=\'50\' r=\'1\' fill=\'%232C3E50\' opacity=\'0.05\'/></pattern></defs><rect width=\'100\' height=\'100\' fill=\'url(%23grain)\'/></svg>")',
          opacity: 0.08,
        }
      }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <SectionTitle variant="h2" sx={{ fontSize: { xs: '2.2rem', md: '2.8rem' }, color: '#2980b9', mb: 2 }}>
              Visit Us
            </SectionTitle>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto', mt: 1 }}>
              Find our office location and get directions easily.
            </Typography>
          </Box>
        </Container>
        <Box sx={{ width: '100%', position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
          <Box
            sx={{
              width: '100%',
              mx: 'auto',
              height: { xs: 260, sm: 320, md: 400, lg: 480 },
              minHeight: 200,
              maxHeight: { xs: 320, sm: 400, md: 480, lg: 600 },
              borderRadius: 0,
              overflow: 'hidden',
              boxShadow: '0 4px 24px rgba(44,62,80,0.10)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <MapContainer
              center={[businessLocation.lat, businessLocation.lng]}
              zoom={15}
              style={{ width: '100%', height: '100%' }}
              scrollWheelZoom={false}
              attributionControl={false}
            >
              <TileLayer
                attribution=''
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <Marker position={[businessLocation.lat, businessLocation.lng]}>
                <Popup>
                  Our Office Location
                </Popup>
              </Marker>
            </MapContainer>
          </Box>
        </Box>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#2980b9' }}>
              30 Mossford Green BARKINGSIDE IG6 2BJ, UK
            </Typography>
            <Button
              variant="contained"
              size="large"
              sx={{ borderRadius: '50px', px: 4, py: 1.5, fontWeight: 600, background: '#2980b9', color: 'white', boxShadow: '0 4px 16px rgba(52,152,219,0.18)', '&:hover': { background: '#217dbb' } }}
              href={googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<Visibility />}
            >
              Go to Map
            </Button>
          </Box>
        </Container>
      </Box>


      {/* Call to Action Section */}
      {/* <Box sx={{
        background: '#2C3E50', // Solid professional dark color
        color: 'white',
        py: 12,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 100 100\'><defs><pattern id=\'dots\' width=\'20\' height=\'20\' patternUnits=\'userSpaceOnUse\'><circle cx=\'10\' cy=\'10\' r=\'2\' fill=\'%23ffffff\' opacity=\'0.1\'/></pattern></defs><rect width=\'100\' height=\'100\' fill=\'url(%23dots)\'/></svg>")',
          opacity: 0.3,
        }
      }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 2 }}>
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 3 }}>
              Ready to Start Your Project?
            </Typography>
            <Typography variant="h6" sx={{ mb: 5, opacity: 0.9, lineHeight: 1.6 }}>
              Get in touch with our expert team today for a free consultation and quote.
              Let's turn your construction dreams into reality.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button
                variant="contained"
                size="large"
                startIcon={<Phone />}
                onClick={() => navigate('/contact')}
                sx={{
                  py: 2,
                  px: 4,
                  fontSize: '1.1rem',
                  borderRadius: '50px',
                  background: 'white',
                  color: '#2C3E50', // Professional dark text
                  fontWeight: 600,
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.9)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                Contact Us Today
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<Email />}
                onClick={() => navigate('/contact')}
                sx={{
                  py: 2,
                  px: 4,
                  fontSize: '1.1rem',
                  borderRadius: '50px',
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  color: 'white',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: 'white',
                    background: 'rgba(255, 255, 255, 0.1)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                Get Free Quote
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box> */}
    </>
  );
}

export default Home;